import type { RemovalGroup } from "../cableEditGeometry";
import type { RemovedBatch, RemovedDevice } from "./types";

export interface BuildRemovedBatchesArgs {
  removed: RemovedDevice[];
  groups: RemovalGroup[];
}

export type BuildRemovedBatches = (
  args: BuildRemovedBatchesArgs,
) => RemovedBatch[];

const UNGROUPED_ID = "rm-ungrouped";

/**
 * Newest first: the thing he most likely wants back is the thing he just did.
 *
 * Anything struck before removals were recorded as groups — or by a path that
 * never made one — is collected into one last batch rather than dropped. A
 * struck device that appears in no section is a device he cannot get back.
 */
export const buildRemovedBatches: BuildRemovedBatches = ({ removed, groups }) => {
  // Indexed under BOTH keys. A group records the durable device key, and the
  // list is handed devices by their address — so matching on one alone drops
  // every struck device out of its removal and into the leftovers drawer, which
  // reads as "I never did that" rather than as a key mismatch.
  const byId = new Map<string, RemovedDevice>();
  removed.forEach((device) => {
    byId.set(device.destinationId, device);
    if (device.deviceKey) byId.set(device.deviceKey, device);
  });
  const claimed = new Set<string>();

  const batches: RemovedBatch[] = [];
  groups.forEach((group, index) => {
    const devices = group.destinationIds
      .map((destinationId) => byId.get(destinationId))
      .filter((device): device is RemovedDevice => Boolean(device));
    devices.forEach((device) => claimed.add(device.destinationId));
    if (!devices.length) return;
    batches.push({
      id: group.id,
      ordinal: index + 1,
      devices,
      fromCircuits: [...new Set(devices.map((device) => device.circuitId))],
    });
  });

  batches.reverse();

  // Always last, whatever its age: it is the leftovers drawer, not a removal.
  const loose = removed.filter((device) => !claimed.has(device.destinationId));
  if (loose.length) {
    batches.push({
      id: UNGROUPED_ID,
      ordinal: 0,
      devices: loose,
      fromCircuits: [...new Set(loose.map((device) => device.circuitId))],
    });
  }

  return batches;
};
